import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ModalRoute from "../../components/common/ModalRoute";
import ApiAccessEmptyState from "../../components/api/ApiAccessEmptyState";
import ApiRequestDetail from "../../components/api/ApiRequestDetail";
import ApiUsageCharts from "../../components/api/ApiUsageCharts";
import AgentInstructionsModal from "../../components/api/AgentInstructionsModal";
import ApiHelpLinks from "../../components/api/ApiHelpLinks";
import { apiTokensService } from "../../services/apiTokens";
import { getApiErrorMessage } from "../../utils/apiErrors";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import { useToast } from "../../contexts/ToastContext";

function formatRelativeTime(iso) {
  if (!iso) return "Never used";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never used";
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 60) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}


const CreateApiKeyModal = ({ onCreated, onClose }) => {
  const { projects } = useSelector((state) => state.project);
  const { brands } = useSelector((state) => state.brand);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("account");
  const [projectHash, setProjectHash] = useState("");
  const [brandHash, setBrandHash] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const data = { name, scope };
    if (scope === "project") data.project_hash_write = projectHash;
    if (scope === "brand") data.brand_hash_write = brandHash;
    apiTokensService.createToken(
      data,
      (res) => {
        setSaving(false);
        setCreated(res.data);
      },
      (err) => {
        setSaving(false);
        setError(getApiErrorMessage(err, "Could not create API key"));
      },
    );
  };

  const copyCreated = async () => {
    if (!created?.token) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch (_) {}
  };

  if (created?.token) {
    return (
      <div className="p-6 space-y-4">
        <h3 className="text-lg font-semibold text-theme-text">API key created</h3>
        <p className="text-sm text-theme-text opacity-80">
          Copy this key now. It will not be shown again.
        </p>
        <code className="block text-xs font-mono break-all rounded border border-theme-secondary bg-theme-secondary bg-opacity-40 p-3 text-theme-text">
          {created.token}
        </code>
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={copyCreated}
            className="flex-1 py-2 rounded-md border border-theme-secondary text-theme-text"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={() => onCreated?.(created)}
            className="flex-1 py-2 rounded-md bg-theme-primary text-theme-button"
            style={{ backgroundColor: "var(--theme-primary)" }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-6 space-y-4">
      <h3 className="text-lg font-semibold text-theme-text">Create API key</h3>
      {error && (
        <div className="rounded border border-red-500 border-opacity-40 bg-red-500 bg-opacity-10 px-3 py-2 text-sm text-theme-text">
          {error}
        </div>
      )}
      <div>
        <label className="block text-sm text-theme-text mb-1">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text"
          placeholder="Production"
        />
      </div>
      <div>
        <label className="block text-sm text-theme-text mb-1">Access level</label>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text"
        >
          <option value="account">Account</option>
          <option value="project">Project</option>
          <option value="brand">Brand</option>
        </select>
      </div>
      {scope === "project" && (
        <div>
          <label className="block text-sm text-theme-text mb-1">Project</label>
          <select
            value={projectHash}
            onChange={(e) => setProjectHash(e.target.value)}
            required
            className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text"
          >
            <option value="">Select project</option>
            {projects.map((p) => (
              <option key={p.hash} value={p.hash}>{p.name}</option>
            ))}
          </select>
        </div>
      )}
      {scope === "brand" && (
        <div>
          <label className="block text-sm text-theme-text mb-1">Brand</label>
          <select
            value={brandHash}
            onChange={(e) => setBrandHash(e.target.value)}
            required
            className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text"
          >
            <option value="">Select brand</option>
            {brands.map((b) => (
              <option key={b.hash} value={b.hash}>{b.name || b.hash}</option>
            ))}
          </select>
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 py-2 rounded-md border border-theme-secondary text-theme-text">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 rounded-md bg-theme-primary text-theme-button disabled:opacity-50"
          style={{ backgroundColor: "var(--theme-primary)" }}
        >
          {saving ? "Creating..." : "Create"}
        </button>
      </div>
    </form>
  );
};

const AccountApi = ({ currentUser }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [tokens, setTokens] = useState([]);
  const [showRevoked, setShowRevoked] = useState(false);
  const [selectedHash, setSelectedHash] = useState("");
  const [loading, setLoading] = useState(true);
  const hasApi = Boolean(currentUser?.features?.api);

  const refresh = ({ selectHash, quiet } = {}) => {
    if (!quiet) setLoading(true);
    apiTokensService.listTokens(
      (res) => {
        const rows = res.data?.results || res.data || [];
        const list = Array.isArray(rows) ? rows : [];
        setTokens(list);
        if (selectHash) {
          setSelectedHash(selectHash);
        } else if (!selectedHash && list[0]) {
          setSelectedHash(list[0].hash);
        }
        setLoading(false);
      },
      () => {
        setTokens([]);
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    if (!hasApi) return;
    refresh();
  }, [hasApi]);

  if (!hasApi) {
    return <ApiAccessEmptyState />;
  }

  const selected = tokens.find((t) => t.hash === selectedHash);

  const handleCreated = (token) => {
    showToast("API key was created");
    navigate("/account/api/");
    refresh({ selectHash: token?.hash, quiet: true });
  };

  const revoke = () => {
    if (!selected || !window.confirm("Revoke this API key?")) return;
    apiTokensService.revokeToken(selected.hash, () => refresh({ quiet: true }), () => {});
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <OrbitalLoader size={96} text="Loading API keys..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 h-[calc(100vh-11rem)] min-h-[28rem]">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
        <div className="lg:col-span-1 border border-theme-secondary rounded-lg overflow-hidden flex flex-col min-h-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-theme-secondary shrink-0">
            <h2 className="text-sm font-semibold text-theme-text">API keys</h2>
            <button
              type="button"
              onClick={() => navigate("/account/api/new")}
              className="text-sm text-theme-primary hover:opacity-80"
            >
              Create key
            </button>
          </div>
          <div className="divide-y divide-theme-secondary overflow-y-auto flex-1 min-h-0">
            {tokens.length === 0 && (
              <p className="p-4 text-sm text-theme-text opacity-60">No API keys yet.</p>
            )}
            {(showRevoked ? tokens : tokens.filter((token) => token.is_active)).map((token) => (
              <button
                key={token.hash}
                type="button"
                onClick={() => setSelectedHash(token.hash)}
                className={`w-full text-left px-4 py-3 ${
                  selectedHash === token.hash ? "bg-theme-secondary bg-opacity-50" : "hover:bg-black hover:bg-opacity-5"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-theme-text truncate">
                      {token.name || token.hash}
                    </div>
                    <div className="text-xs text-theme-text opacity-60 mt-0.5">
                      {token.scope}
                      {!token.is_active ? " · revoked" : ""}
                      {token.brand_name ? ` · ${token.brand_name}` : ""}
                      {token.project_name && token.scope === "project" ? ` · ${token.project_name}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-theme-text">
                      {token.request_count_30d ?? 0}
                    </div>
                    <div className="text-[11px] text-theme-text opacity-50">reqs / 30d</div>
                  </div>
                </div>
                <div className="text-[11px] text-theme-text opacity-50 mt-1">
                  {formatRelativeTime(token.last_used_at)}
                </div>
              </button>
            ))}
            {tokens.some((token) => !token.is_active) && (
              <button
                type="button"
                onClick={() => setShowRevoked((open) => !open)}
                className="w-full text-left px-4 py-3 text-sm text-theme-text opacity-60 hover:opacity-100"
              >
                {showRevoked
                  ? "Hide revoked"
                  : `Show ${tokens.filter((token) => !token.is_active).length} revoked`}
              </button>
            )}
          </div>
          <div className="border-t border-theme-secondary shrink-0 p-2">
            <ApiHelpLinks
              showPlayground
              onAgentClick={() => navigate("/account/api/agent-instructions/")}
            />
          </div>
        </div>

        <div className="lg:col-span-2 border border-theme-secondary rounded-lg overflow-hidden flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 space-y-5">
            {!selected ? (
              <p className="text-sm text-theme-text opacity-60">Select a key to view usage and configuration.</p>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-semibold text-theme-text">{selected.name || selected.hash}</h2>
                    {selected.token_prefix && (
                      <code className="block text-xs font-mono text-theme-text opacity-70 mt-1.5 truncate">
                        {selected.token_prefix}********
                      </code>
                    )}
                  </div>
                  {selected.is_active && (
                    <button type="button" onClick={revoke} className="text-sm text-red-400 hover:opacity-80 shrink-0">
                      Revoke
                    </button>
                  )}
                </div>

                <ApiUsageCharts
                  tokenHash={selected.hash}
                  layout="panel"
                  currentUser={currentUser}
                  onSelectRequest={(row) => navigate(`/account/api/requests/${row.hash}/`)}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ModalRoute
        path="/account/api/new"
        parentPath="/account/api/"
        element={
          <CreateApiKeyModal
            onCreated={handleCreated}
            onClose={() => navigate("/account/api/")}
          />
        }
        maxWidth="max-w-lg"
        heightClass="h-auto max-h-[90vh]"
        noPadding
      />
      <ModalRoute
        path="/account/api/agent-instructions"
        parentPath="/account/api/"
        element={
          <AgentInstructionsModal onClose={() => navigate("/account/api/")} />
        }
        maxWidth="max-w-2xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
      <ModalRoute
        path="/account/api/requests/:hash"
        parentPath="/account/api/"
        element={<ApiRequestDetailWrapper />}
        maxWidth="max-w-3xl"
        heightClass="h-[85vh] max-h-[85vh]"
        noPadding
      />
    </div>
  );
};

function ApiRequestDetailWrapper() {
  const match = useLocation().pathname.match(/\/account\/api\/requests\/([^/]+)/);
  const hash = match?.[1];
  if (!hash) return null;
  return <ApiRequestDetail hash={hash} />;
}

export default AccountApi;
